import { Helmet } from 'react-helmet';
import styles from './Features.module.css';
import { FEATURES_PAGE_FEATURES } from '../content/featuresCatalog';
import { useAuth } from '../contexts/AuthContext';
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

export default function Features() {
  const { currentUser } = useAuth();

  const nextBackendBaseUrl = useMemo(() => {
    return import.meta.env.VITE_NEXT_BACKEND_URL || '';
  }, []);

  const [xBusy, setXBusy] = useState(false);
  const [xMessage, setXMessage] = useState('');

  const { data: xConnected = false } = useQuery<boolean>({
    queryKey: ['xStatus', nextBackendBaseUrl],
    queryFn: async () => {
      try {
        const res = await fetch(`${nextBackendBaseUrl}/api/x/status`, { method: 'GET', credentials: 'include' });
        const json = await res.json();
        return Boolean(json?.connected);
      } catch {
        return false;
      }
    },
  });

  const buildRandomFeaturePost = () => {
    const feature =
      FEATURES_PAGE_FEATURES[Math.floor(Math.random() * FEATURES_PAGE_FEATURES.length)];

    const origin = window.location.origin;
    const featureUrl = `${origin}/features#${feature.id}`;
    const buyUrl = `${origin}/buy`;

    const desc = feature.description || '';
    const trimmedDesc = desc.length > 140 ? `${desc.slice(0, 137)}...` : desc;

    const text = `${feature.title}: ${trimmedDesc}\n\nLearn more: ${featureUrl}\nBuy: ${buyUrl}`;

    // Keep it safe for X's 280 char limit
    return text.length > 280 ? text.slice(0, 277) + '...' : text;
  };

  const connectX = async () => {
    setXMessage('');
    if (!currentUser) {
      setXMessage('Please log in first.');
      return;
    }

    setXBusy(true);
    try {
      const idToken = await currentUser.getIdToken();
      const res = await fetch(`${nextBackendBaseUrl}/api/x/connect`, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${idToken}`,
        },
        credentials: 'include',
      });
      const json = await res.json();
      if (!res.ok) {
        throw new Error(json?.error || 'Failed to start X OAuth');
      }
      if (!json?.authUrl) {
        throw new Error('Missing authUrl');
      }

      window.location.href = json.authUrl;
    } catch (e) {
      setXMessage(e.message || 'Failed to connect X');
    } finally {
      setXBusy(false);
    }
  };

  const postRandomFeatureToX = async () => {
    setXMessage('');
    if (!currentUser) {
      setXMessage('Please log in first.');
      return;
    }
    if (!xConnected) {
      setXMessage('Please connect X first.');
      return;
    }

    setXBusy(true);
    try {
      const text = buildRandomFeaturePost();
      const idToken = await currentUser.getIdToken();

      const res = await fetch(`${nextBackendBaseUrl}/api/x/post`, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          authorization: `Bearer ${idToken}`,
        },
        credentials: 'include',
        body: JSON.stringify({ text }),
      });

      const json = await res.json();
      if (!res.ok) {
        throw new Error(json?.error || 'Failed to post');
      }

      setXMessage('Posted to X.');
    } catch (e) {
      setXMessage(e.message || 'Failed to post');
    } finally {
      setXBusy(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Features | Smart Contact Management & Groups | MyPeopleDatabase</title>
        <meta
          name="description"
          content="Explore MyPeopleDatabase features including smart groups, advanced contact management, privacy controls, and personal knowledge management (PKM) to organize everything you know about the people in your life."
        />
        <meta
          name="keywords"
          content="contact manager features, smart groups, address book, MyPeopleDatabase, PKM"
        />
      </Helmet>
      <div className={styles.container}>
        <div className={styles.overlay} />
        <div className={styles.content}>
          <h1 className={styles.title}>
            MyPeopleDatabase Features: Complete Address Book & Contact Manager Solution
          </h1>

          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '16px' }}>
            <button
              onClick={connectX}
              disabled={xBusy || !currentUser}
              style={{ padding: '10px 12px', borderRadius: '8px' }}
            >
              {xConnected ? 'X Connected' : 'Connect X'}
            </button>
            <button
              onClick={postRandomFeatureToX}
              disabled={xBusy || !currentUser || !xConnected}
              style={{ padding: '10px 12px', borderRadius: '8px' }}
            >
              Post random feature to X
            </button>
            {xMessage && <span style={{ color: 'white', fontSize: '14px' }}>{xMessage}</span>}
          </div>

          <div className={styles.featuresContainer}>
            {FEATURES_PAGE_FEATURES.map(feature => (
              <div key={feature.id} className={styles.feature}>
                <a id={feature.id} />
                <h2>{feature.title}</h2>
                <p>{feature.description}</p>
                {feature.link && (
                  <a href={feature.link.href} className={styles.featureLink}>
                    {feature.link.text}
                  </a>
                )}
              </div>
            ))}
          </div>

          <div className={styles.bulletFeatures}>
            <h2>Key Capabilities</h2>
            <ul>
              <li>Comprehensive digital address book for personal and business use</li>
              <li>Intelligent contact manager with relationship tracking</li>
              <li>Custom groups and organizational categories</li>
              <li>Import contacts from multiple sources and platforms</li>
              {/* <li>Secure data sharing with controlled access permissions</li> */}
            </ul>
          </div>

          <div className={styles.bulletFeatures}>
            <h2>Personal Knowledge Management (PKM)</h2>
            <p>
              MyPeopleDatabase can act as the people and relationship layer of your personal
              knowledge management (PKM) system, giving you a structured home for everything you
              know about the people in your life.
            </p>
            <a href="/pkm" className={styles.featureLink}>
              Learn more about PKM and MyPeopleDatabase
            </a>
          </div>

          <div className={styles.bulletFeatures}>
            <h2>For Networkers</h2>
            <p>
              Professional networkers need more than a contact list—they need a complete relationship
              management system. MyPeopleDatabase helps you organize contacts by events and industries,
              remember crucial details from every conversation, and never let a valuable connection go cold.
            </p>
            <a href="/for-networkers" className={styles.featureLink}>
              See how networkers use MyPeopleDatabase →
            </a>
          </div>

          <div className={styles.bulletFeatures}>
            <h2>Benefits</h2>
            <ul>
              <li>Keep your contact list current while preserving memories</li>
              <li>
                Remove people who have passed from your active contact list but still retain them in
                your database
              </li>
              <li>Access your important contacts quickly without clutter</li>
              <li>Maintain a digital legacy of all your connections</li>
              <li>
                Maintain complete ownership of your data and relationships instead of surrendering
                them to platforms
              </li>
              <li>Reduce confusion when reaching out to contacts</li>
            </ul>
          </div>
        </div>
      </div>
    </>
  );
}
